import { useLog } from '@/features/shared/Hooks';
import {
  PowerAppsPostMessageEvents,
  usePowerAppConnectMessageListener,
} from '@/hooks/power-app-connect/usePowerAppConnectMessageListener';
import { serializeQueryParams, useWindowLocation } from '@/hooks/useWindowLocation';
import { getOr } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { usePageModels } from '@/context/PageModels';
import { POWERAPPS_BASE_URL } from '@/utils/constants';
import { usePowerApps } from '@/context';

interface PowerAppsUrlPostMessagePayload {
  href: string;
}

interface PowerAppUrlInfo {
  basePath: '' | string;
  secondaryPath: '' | string;
}

export const cleanStartSlash = (url?: string) => url?.replace(/^\//, '');
export const cleanEndSlash = (url?: string) => url?.replace(/\/$/, '');
const parseAllowedPAPaths = (config: any) => {
  const powerAppsAllowedUrlPaths = config?.powerapps?.powerAppsAllowedUrlPaths;
  try {
    return JSON.parse(powerAppsAllowedUrlPaths || '[]');
  } catch (e) {
    console.error(`An error occurred trying to parse ${powerAppsAllowedUrlPaths}`, e);
    return [];
  }
};
const findContainedPAPath = (pathname: string, paths: string[]): string | undefined => {
  // Split the pathname into segments
  const segments = pathname.split('/');

  // Find the first matching path in the array
  return paths.find((path) => segments.includes(path));
};

export const POWER_APP_PATH_PARAM_NAME = 'p';

export const getPowerAppUrlInfo = (href: string, segments: string[]): PowerAppUrlInfo => {
  const regex = new RegExp(`(${segments.join('|')})(.*)`);
  const matches = href.match(regex);
  return {
    basePath: getOr('', 1, matches),
    secondaryPath: getOr('', 2, matches),
  };
};

/**
 * Hook that handles parsing and constructing PowerApps URLs based on query parameters and configuration
 *
 * @returns {Object} Object containing utility functions:
 *   - getPASrcUrl: Function that constructs the full PowerApps source URL by combining:
 *     - Base PowerApps URL from constants
 *     - Allowed path from current location that matches configuration
 *     - Optional secondary path from query parameters
 */
export const usePowerAppsParseQueryParams = () => {
  // Get the 'p' query parameter value which contains the secondary path
  const [paIframePathParam] = useQueryParam(POWER_APP_PATH_PARAM_NAME, StringParam);

  const location = useLocation();
  const { config } = usePageModels();

  // Parse allowed PowerApps paths from config, memoized to avoid re-parsing
  const paAllowedPaths = useMemo(() => parseAllowedPAPaths(config), [config]);

  /**
   * Constructs the full PowerApps source URL by combining base URL, allowed path, and query params
   * @returns {string} The complete PowerApps URL to load in the iframe
   */
  const getPASrcUrl = useCallback(
    (srcUrl?: string) => {
      const cleanEndSlashBaseUrl = cleanEndSlash(POWERAPPS_BASE_URL);
      const containedPAPath = findContainedPAPath(location.pathname, paAllowedPaths);

      if (!containedPAPath) {
        return `${cleanEndSlashBaseUrl}/${cleanStartSlash(srcUrl)}`;
      }

      // Construct base URL by combining PowerApps base URL with the matching allowed path
      const baseUrl = `${cleanEndSlashBaseUrl}/${cleanStartSlash(containedPAPath)}/`;

      // If no secondary path parameter exists, return just the base URL
      if (!paIframePathParam) {
        return baseUrl;
      }

      // Combine base URL with secondary path from query params, cleaning slashes
      return `${baseUrl}${cleanStartSlash(paIframePathParam)}`;
    },
    [location.pathname, paAllowedPaths, paIframePathParam],
  );

  return { getPASrcUrl };
};

/**
 * Hook that handles deep navigation within PowerApps by managing URL query parameters.
 * It listens for URL update messages from PowerApps and updates the browser URL accordingly.
 *
 * The hook handles two main scenarios:
 * 1. Updating query params on the current page when staying within the same PowerApp
 * 2. Redirecting to a different PowerApp page and setting its query params
 *
 * @returns void
 */
export const usePowerAppsUpdateQueryParamNavigation = (srcUrl: string) => {
  const { config } = usePageModels();
  const { addQueryParams } = useWindowLocation();
  const { getPASrcUrl } = usePowerAppsParseQueryParams();
  // const location = useLocation();
  const history = useHistory();
  const log = useLog({ context: '[PA] Url handler' });
  const mounted = useRef(false);
  const { persistedQueryParams } = usePowerApps();

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // Get allowed PowerApps paths from config
  const paAllowedPaths = useMemo(() => parseAllowedPAPaths(config), [config]);

  /**
   * Handles URL update messages from PowerApps by updating browser URL state
   * @param payload - Message payload containing the new href from PowerApps
   */
  const onPowerAppsUpdateParentUrl = useCallback(
    (payload: PowerAppsUrlPostMessagePayload) => {
      // Exit if no paths are configured
      if (!paAllowedPaths.length) return;

      // Exit if the component is not mounted
      if (!mounted.current) return;

      // Extract base path and secondary path from the PowerApp URL
      const { basePath, secondaryPath } = getPowerAppUrlInfo(payload.href, paAllowedPaths);

      // Skip if the current URL already matches the requested URL
      if (getPASrcUrl() === payload.href) return;

      if (srcUrl.includes(basePath)) {
        log('Adding query params to existing url', { [POWER_APP_PATH_PARAM_NAME]: secondaryPath });
        return addQueryParams({ [POWER_APP_PATH_PARAM_NAME]: secondaryPath });
      }

      // Case 2: Redirect to different PowerApp page
      log('Redirecting to the right section', { [POWER_APP_PATH_PARAM_NAME]: secondaryPath });
      history.push({
        pathname: basePath,
        search: `?${serializeQueryParams({ [POWER_APP_PATH_PARAM_NAME]: secondaryPath })}`,
      });
      Promise.resolve().then(() => {
        persistedQueryParams.setQueryParams({
          search: `?${serializeQueryParams({ [POWER_APP_PATH_PARAM_NAME]: secondaryPath })}`,
        });
      });
    },
    [paAllowedPaths, getPASrcUrl, srcUrl, log, history, persistedQueryParams, addQueryParams],
  );

  // Set up message listener for URL update events from PowerApps
  usePowerAppConnectMessageListener(
    onPowerAppsUpdateParentUrl,
    PowerAppsPostMessageEvents.UPDATE_PARENT_URL,
  );
};
